import wcs from '@/scripts/libs/wcs'
import algoliasearch, { SearchClient, SearchIndex } from 'algoliasearch'
import { RequestOptions } from '@algolia/transporter'
import { SearchOptions, SearchResponse } from '@algolia/client-search'
import { createFallbackableCache } from '@algolia/cache-common'
import { createInMemoryCache } from '@algolia/cache-in-memory'
import { createBrowserLocalStorageCache } from '@algolia/cache-browser-local-storage'

type Option = {
  label: string
  description?: string
}

export type OptionsObject = {
  [key: string]: Option
}

export interface AlgoliaTranslations {
  facets: Array<{
    key?: string
    label: string
    options?: Array<{ key: string; description: string }> | OptionsObject
  }>
  allPromos: string
  sort_by: {
    label: string
    options: Array<{ key: string; label: string }>
  }
}

export class Algolia {
  private client: SearchClient
  private index: SearchIndex

  private readonly groupedIndexName
  private readonly ungroupedIndexName
  translations: AlgoliaTranslations | undefined = undefined

  constructor() {
    const {
      algolia: { apiKey, applicationId, groupedIndex, ungroupedIndex },
    } = wcs.getStoreConfig()

    this.groupedIndexName = groupedIndex
    this.ungroupedIndexName = ungroupedIndex

    this.client = algoliasearch(applicationId, apiKey, {
      responsesCache: createFallbackableCache({
        caches: [
          createBrowserLocalStorageCache({
            key: `algolia-responses-${applicationId}-${1}`,
            timeToLive: 3600,
          }),
          createInMemoryCache(),
        ],
      }),
    })
    this.index = this.client.initIndex(this.groupedIndexName)
  }

  async multipleGroupedSearch(queries: Array<{ params: SearchOptions; indexName?: string }>) {
    return this.client.multipleQueries(
      queries.map(({ indexName, ...rest }) => ({
        ...rest,
        indexName: this.getGroupedIndexNameOrDefault(indexName),
      }))
    )
  }

  async multipleSearch(queries: Array<{ params: SearchOptions; indexName: string }>) {
    return await this.client.multipleQueries(queries)
  }

  async groupedSearch<T>({
    query = '',
    index = undefined,
    options = {},
  }: {
    query?: string
    index?: string
    options?: RequestOptions & SearchOptions
  }) {
    this.setGroupedIndex(index)
    const res = await this.index.search<T>(query, options)

    if (index) {
      this.resetGroupedIndex()
    }

    return res
  }

  async ungroupedSearch<T>({
    sortOrder,
    queries,
  }: {
    sortOrder?: string
    queries: Array<SearchOptions>
  }) {
    const index = this.getUngroupedIndexNameOrDefault(sortOrder)

    return await this.client.multipleQueries<T>(
      queries.map((query) => ({ ...query, indexName: index }))
    )
  }

  private setGroupedIndex(sortOrder?: string) {
    if (!sortOrder) {
      this.index = this.client.initIndex(this.groupedIndexName)
      return
    }

    this.index = this.client.initIndex(`${this.groupedIndexName}${sortOrder}`)
  }

  private resetGroupedIndex() {
    this.setGroupedIndex()
  }

  getGroupedIndexNameOrDefault(suffix?: string) {
    return suffix ? `${this.groupedIndexName}${suffix}` : this.groupedIndexName
  }

  getUngroupedIndexNameOrDefault(suffix?: string) {
    return suffix ? `${this.ungroupedIndexName}${suffix}` : this.ungroupedIndexName
  }
  static generateModule() {
    if (window.algolia) {
      return window.algolia
    }
    return new Algolia()
  }

  isOptionsObject(
    options: Array<{ key: string; description: string }> | OptionsObject
  ): options is OptionsObject {
    return !Array.isArray(options)
  }
}

window.algolia = Algolia.generateModule()

export default window.algolia

export { SearchResponse }
